import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, ConfirmDialog, Input } from '@/shared/components';
import { useApi, useApiProblem } from '@/shared/api';
import { useAuth } from '@/shared/auth';

function CheckInEmailPanelContent({
  bookingId,
  propertyId,
  email,
  canSend,
  departed,
  queuedAt,
  paymentLink = false,
}: {
  bookingId: string;
  propertyId: string;
  email: string | null;
  canSend: boolean;
  departed: boolean;
  queuedAt?: string | null;
  paymentLink?: boolean;
}) {
  const { t } = useTranslation('manualBooking');
  const api = useApi();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [draft, setDraft] = useState(email ?? '');
  const [confirm, setConfirm] = useState(false);
  const operationKey = `stayon.checkin.${user?.tenantId ?? 'unknown'}.${bookingId}`;
  const operation = useRef(sessionStorage.getItem(operationKey) ?? crypto.randomUUID());
  const [unresolved, setUnresolved] = useState(() => sessionStorage.getItem(operationKey) !== null);
  const [recovered, setRecovered] = useState(false);
  useEffect(() => {
    if (!sessionStorage.getItem(operationKey)) return;
    let active = true;
    const checkedOperation = operation.current;
    void api.bookings
      .checkCheckInEmail(bookingId, checkedOperation)
      .then((found) => {
        if (!active || !found || sessionStorage.getItem(operationKey) !== checkedOperation) return;
        sessionStorage.removeItem(operationKey);
        setUnresolved(false);
        setRecovered(true);
        operation.current = crypto.randomUUID();
        void qc.invalidateQueries({ queryKey: ['onboardingSupport', bookingId, propertyId] });
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [api, bookingId, operationKey, propertyId, qc]);
  useEffect(() => {
    setDraft(email ?? '');
  }, [email]);
  const refresh = () =>
    qc.invalidateQueries({ queryKey: ['onboardingSupport', bookingId, propertyId] });
  const contact = useMutation({
    mutationFn: () => api.bookings.updateGuestContact(bookingId, draft.trim() || null),
    onSuccess: async () => {
      await Promise.all([
        refresh(),
        qc.invalidateQueries({ queryKey: ['guests'] }),
        qc.invalidateQueries({ queryKey: ['bookings'] }),
        qc.invalidateQueries({ queryKey: ['calendar'] }),
      ]);
    },
    networkMode: 'always',
  });
  const send = useMutation({
    mutationFn: (submittedOperation: string) => {
      sessionStorage.setItem(operationKey, submittedOperation);
      setUnresolved(true);
      return api.bookings.sendCheckInEmail(bookingId, submittedOperation, email!);
    },
    onSuccess: async (_result, submittedOperation) => {
      if (sessionStorage.getItem(operationKey) !== submittedOperation) return;
      setConfirm(false);
      sessionStorage.removeItem(operationKey);
      setUnresolved(false);
      operation.current = crypto.randomUUID();
      await refresh();
    },
    networkMode: 'always',
  });
  const problem = useApiProblem(contact.error ?? send.error);
  const sendProblem = useApiProblem(send.error);
  const pending = contact.isPending || send.isPending;
  return (
    <div className="border-hairline space-y-3 border-t pt-3">
      <label className="block text-sm font-semibold">
        {t('guestEmail')}
        <Input
          type="email"
          value={draft}
          disabled={pending}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t('addEmail')}
        />
      </label>
      <Button
        variant="ghost"
        className="min-h-11"
        loading={contact.isPending}
        disabled={pending || draft.trim() === (email ?? '')}
        onClick={() => contact.mutate()}
      >
        {t('saveContact')}
      </Button>
      {!email && <p className="text-ink-soft text-sm">{t('missingEmail')}</p>}
      {!email && paymentLink && <p className="text-ink-soft text-sm">{t('afterPaymentWaiting')}</p>}
      {email && <p className="text-ink-soft break-all text-sm">{t('recipient', { email })}</p>}
      {(queuedAt || recovered) && (
        <p role="status" className="text-ink-soft text-sm">
          {t('queued')}
        </p>
      )}
      {unresolved && (
        <p role="status" className="text-ink-soft text-sm">
          {t('sendUnresolved')}
        </p>
      )}
      {problem && (
        <p role="alert" className="text-sealwax text-sm">
          {problem.title === 'guest_contact_active_link'
            ? t('activeLink')
            : problem.title === 'guest_email_changed'
              ? t('changedEmail')
              : t('unknown')}
        </p>
      )}
      {canSend && !departed && (
        <Button
          variant="brand"
          className="min-h-11 w-full"
          disabled={!email || pending || draft.trim() !== email}
          onClick={() => setConfirm(true)}
        >
          {unresolved ? t('retrySend') : t('send')}
        </Button>
      )}
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={t('sendTitle')}
        description={
          <>
            <span>{t('sendDescription', { email: email ?? '' })}</span>
            {sendProblem && (
              <span role="alert" className="text-sealwax block">
                {sendProblem.title === 'guest_email_changed'
                  ? t('changedEmail')
                  : sendProblem.status === 0
                    ? t('unknown')
                    : (sendProblem.detail ?? t('sendFailed'))}
              </span>
            )}
          </>
        }
        confirmLabel={unresolved ? t('retrySend') : t('confirmSend')}
        variant="brand"
        footerClassName="flex-wrap"
        buttonClassName="min-h-11"
        loading={send.isPending}
        onConfirm={() => send.mutate(operation.current)}
      />
    </div>
  );
}

export function CheckInEmailPanel(props: Parameters<typeof CheckInEmailPanelContent>[0]) {
  const { user } = useAuth();
  return (
    <CheckInEmailPanelContent
      key={`${user?.tenantId ?? 'unknown'}:${props.bookingId}`}
      {...props}
    />
  );
}
